import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Carousel, CarouselItem } from 'reactstrap';
import minBy from 'lodash/minBy';
import { Helmet } from 'react-helmet';
import LargestCol from 'components/LargestCol';
import { getBookingEnginePath } from 'utils/routeUtils';
import { Redirect } from 'react-router';
import ImageSliderContainer from 'containers/ImageSliderContainer';
import styled from 'styled-components';
import { SLIDER_IMAGES, HERO_ITEMS } from 'utils/images';
import PromotionsButton from './styled/PromotionsButton';
import Promotions from './styled/Promotions';
import Hero from './styled/Hero';
import ConfirmationGrid from './styled/ConfirmationGrid';
import BookingConfirmation from './BookingConfirmation';
import WcCarouselControls from './WcCarouselControls';
import {
  PROPERTIES,
  PROPERTY_BETHLEHEM,
  PROPERTY_PROMOTIONS_URL,
  FLOODLIGHT_ID
} from 'utils/constants';
import { gtag } from 'utils/googleAnalytics';

const PromotionsBanner = styled.div`
  background-color: ${props => props.theme.buttons.primary.backgroundColor};
  color: #fff;
  margin-top: 1.25em;
  padding: 0.5em 1em 0.5em 1em;

  p {
    margin: 0;
  }

  @media screen and (min-width: ${props => props.theme.breakpoints.small}) {
    display: flex;
    align-items: center;
  }
`;

const StyledDiv = styled.div`
  @media screen and (max-width: ${props => props.theme.breakpoints.small}) {
    min-height: 13em;
    overflow-y: auto;
  }
`;

const StyledTimes = styled.div`
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
`;

const StyledHeader = styled.h4`
  @media screen and (max-width: ${props => props.theme.breakpoints.small}) {
    overflow-x: auto;
    max-width: 100%;
    white-space: nowrap;
  }
`;

export class Confirmation extends Component {
  componentWillUnmount() {
    this.state.unbindLeaveHook();
  }

  state = {
    activeIndex: 0,
    animating: false,
    numberOfItems: 0,
    unbindLeaveHook: route => {
      window.onbeforeunload = this.props.resetApp();
    }
  };

  componentDidMount() {
    const { property, selectedRoom, guestInfo, hotelReservation } = this.props;

    // Bethlehem bookings should submit floodlight tag event.
    if (
      FLOODLIGHT_ID &&
      hotelReservation &&
      property.toLowerCase() === PROPERTY_BETHLEHEM
    ) {
      // Find the lowest roomRate.
      const minRoomRate = minBy(
        selectedRoom.roomRates,
        roomRate => roomRate.total.amount
      );
      const total = minRoomRate.total.amount;

      gtag('event', 'purchase', {
        allow_custom_scripts: true,
        value: total / 100,
        transaction_id: hotelReservation.id,
        u1: guestInfo ? guestInfo.postalCode : '',
        u2: guestInfo ? guestInfo.state.value : '',
        send_to: `${FLOODLIGHT_ID}/wcrksale/wcrkconf+transactions`
      });
    }
  }

  onExiting = () => {
    this.setState({
      animating: true
    });
  };

  onExited = () => {
    this.setState({
      animating: false
    });
  };

  next = () => {
    const { animating, activeIndex, numberOfItems } = this.state;

    if (animating) {
      return;
    }

    const nextIndex = activeIndex === numberOfItems - 1 ? 0 : activeIndex + 1;

    this.setState({ activeIndex: nextIndex });
  };

  previous = () => {
    const { animating, activeIndex, numberOfItems } = this.state;

    if (animating) {
      return;
    }

    const nextIndex = activeIndex === 0 ? numberOfItems - 1 : activeIndex - 1;

    this.setState({ activeIndex: nextIndex });
  };

  goToIndex = newIndex => {
    if (this.animating) return;
    this.setState({ activeIndex: newIndex });
  };

  printItinerary = () => {
    window.print();
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    const { property } = nextProps;

    return {
      numberOfItems: property ? HERO_ITEMS[property].length : 0
    };
  }

  render() {
    const {
      property,
      selectedRoom,
      guestInfo,
      numberOfGuests,
      hotelReservation,
      reviewAndSubmitForm,
      isWaitListBooking
    } = this.props;
    const { activeIndex } = this.state;

    // Redirect to home page if there is no reservation to display.
    if (!hotelReservation && !isWaitListBooking) {
      return <Redirect to={getBookingEnginePath()} />;
    }

    const sliderSettings = {
      autoplay: true,
      autoplaySpeed: 0,
      speed: 5000,
      cssEase: 'linear',
      prevArrow: null,
      nextArrow: null
    };

    const images = SLIDER_IMAGES[property];
    const items = HERO_ITEMS[property];

    return (
      <div>
        <Helmet>
          <title>Booking Confirmation</title>
        </Helmet>
        <ConfirmationGrid>
          <Row>
            <LargestCol md="12" lg="4">
              <BookingConfirmation
                hotelReservation={hotelReservation}
                guestInfo={guestInfo}
                property={property}
                selectedRoom={selectedRoom}
                numberOfGuests={numberOfGuests}
                printItinerary={this.printItinerary}
                reviewAndSubmitForm={reviewAndSubmitForm}
                isWaitListBooking={isWaitListBooking}
              />
            </LargestCol>
            <LargestCol md="12" lg="8" className="promotions-col">
              <Row>
                <LargestCol xs="12">
                  <Hero>
                    <h4>GET READY FOR YOUR TRIP!</h4>
                    <Carousel
                      activeIndex={activeIndex}
                      next={this.next}
                      previous={this.previous}
                    >
                      {items.map(item => (
                        <CarouselItem
                          onExiting={this.onExiting}
                          onExited={this.onExited}
                          key={item.src}
                          className="carousel-item-height"
                        >
                          <Row>
                            <Col
                              md={6}
                              sm={12}
                              style={{ marginBottom: '0.5em' }}
                            >
                              <img src={item.src} alt={item.alt} />
                            </Col>
                            <Col md={6} sm={12}>
                              <StyledDiv>
                                <StyledHeader>{item.header}</StyledHeader>
                                <p>{item.body}</p>
                                {item.hours && (
                                  <StyledTimes>Hours: {item.hours}</StyledTimes>
                                )}
                                {item.phone && (
                                  <React.Fragment>
                                    <span className="contact">
                                      For Reservations, call{' '}
                                      <span>{item.phone}</span>
                                    </span>
                                  </React.Fragment>
                                )}
                              </StyledDiv>
                            </Col>
                          </Row>
                        </CarouselItem>
                      ))}
                      <WcCarouselControls
                        items={items}
                        activeIndex={activeIndex}
                        next={this.next}
                        previous={this.previous}
                      />
                    </Carousel>
                  </Hero>
                </LargestCol>
              </Row>
              <Row>
                <LargestCol xs="12" className="promotions-container">
                  <Promotions>
                    <ImageSliderContainer
                      settings={sliderSettings}
                      images={images}
                    />
                    <PromotionsBanner>
                      <p>
                        <strong>
                          There's always something new happening at Wind Creek!
                        </strong>{' '}
                        Check out the calendar of events for Wind Creek{' '}
                        {PROPERTIES[property]} to plan your trip around fun
                        gaming promotions.
                      </p>
                      <PromotionsButton
                        className="promotions-button"
                        href={PROPERTY_PROMOTIONS_URL[property]}
                        target="_blank"
                      >
                        PROMOTIONS
                      </PromotionsButton>
                    </PromotionsBanner>
                  </Promotions>
                </LargestCol>
              </Row>
            </LargestCol>
          </Row>
        </ConfirmationGrid>
      </div>
    );
  }
}

Confirmation.propTypes = {
  hotelReservation: PropTypes.shape({
    id: PropTypes.string,
    reservationId: PropTypes.string,
    roomStays: PropTypes.arrayOf(
      PropTypes.shape({
        startDate: PropTypes.string,
        endDate: PropTypes.string,
        total: PropTypes.shape({
          amount: PropTypes.number,
          currencyCode: PropTypes.number,
          decimals: PropTypes.number
        }),
        expectedCharges: PropTypes.shape({
          totalRoomAndRatePackages: PropTypes.number,
          totalTaxesAndFees: PropTypes.number,
          taxInclusive: PropTypes.number,
          decimals: PropTypes.number
        })
      })
    )
  }),
  property: PropTypes.string,
  guestInfo: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    address: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zipCode: PropTypes.string,
    cardType: PropTypes.string,
    cardNumber: PropTypes.string,
    expirationMonth: PropTypes.string,
    expirationYear: PropTypes.number,
    cvv: PropTypes.number
  }),
  numberOfGuests: PropTypes.any,
  resetApp: PropTypes.func,
  reviewAndSubmitForm: PropTypes.shape({
    alerts: PropTypes.arrayOf(PropTypes.string),
    roomFeatures: PropTypes.arrayOf(PropTypes.string),
    traces: PropTypes.arrayOf(PropTypes.string),
    discounts: PropTypes.arrayOf(PropTypes.string)
  }),
  isWaitListBooking: PropTypes.bool
};

export default Confirmation;
